/**
 * API Keys API Client
 * ISO 27001 A.9.4.2 - Procédures de connexion sécurisée
 */

import { fetchWithAuth, safeJsonParse } from '@/utils/api';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';

/**
 * F-003: Type aligné sur Prisma ApiKey (sans keyHash, ajout revokedAt)
 */
export interface ApiKeyResponse {
  id: string;
  tenantId: string;
  companyId: string | null;
  userId: string | null;
  name: string;
  keyPreview: string | null;
  scopes: string[];
  isActive: boolean;
  expiresAt: string | null;
  revokedAt: string | null;
  rateLimit: number;
  lastUsedAt: string | null;
  createdAt: string;
  createdFromIp: string | null;
  createdByEmail: string | null;
}

export interface ApiAccessConfig {
  hasAccess: boolean;
  currentSubscription: string;
  allowedSubscriptions: string[];
  rateLimit: number;
}

export interface ApiKeyStats {
  id: string;
  name: string;
  isActive: boolean;
  usageCount: number;
  lastUsedAt: string | null;
  createdAt: string;
  rateLimit: number;
  expiresAt: string | null;
}

export interface ApiKeyUsage {
  apiKeyId: string;
  period: string;
  totalRequests: number;
  successRate: number;
  errorCount: number;
  avgResponseTime: number;
  topEndpoints: Array<{ endpoint: string; method: string; count: number }>;
  requestsByDay: Array<{ date: string; count: number }>;
}

export interface CreateApiKeyInput {
  name: string;
  scopes?: string[];
  expiresAt?: string;
  isTest?: boolean;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PaginatedApiKeysResponse {
  data: ApiKeyResponse[];
  pagination: PaginationInfo;
}

export const apiKeysApi = {
  getConfig: async (): Promise<ApiAccessConfig> => {
    const response = await fetchWithAuth(`${API_URL}/core/api-keys/config`);
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error?.error?.message || 'Erreur chargement configuration API');
    }
    const json = await safeJsonParse(response);
    return json.data;
  },

  getAll: async (page: number = 1, limit: number = 20): Promise<PaginatedApiKeysResponse> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    const response = await fetchWithAuth(`${API_URL}/core/api-keys?${params}`);
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error?.error?.message || 'Erreur chargement clés API');
    }
    const json = await safeJsonParse(response);
    return {
      data: json.data || [],
      pagination: json.pagination || { page, limit, total: 0, totalPages: 0 },
    };
  },

  getById: async (id: string): Promise<ApiKeyResponse> => {
    const response = await fetchWithAuth(`${API_URL}/core/api-keys/${encodeURIComponent(id)}`);
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error?.error?.message || 'Clé API non trouvée');
    }
    const json = await safeJsonParse(response);
    return json.data;
  },

  getStats: async (id: string): Promise<ApiKeyStats> => {
    const response = await fetchWithAuth(`${API_URL}/core/api-keys/${encodeURIComponent(id)}/stats`);
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error?.error?.message || 'Erreur chargement statistiques');
    }
    const json = await safeJsonParse(response);
    return json.data;
  },

  getUsage: async (id: string, period: string = '7d'): Promise<ApiKeyUsage> => {
    const response = await fetchWithAuth(`${API_URL}/core/api-keys/${encodeURIComponent(id)}/usage?period=${encodeURIComponent(period)}`);
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error?.error?.message || 'Erreur chargement usage');
    }
    const json = await safeJsonParse(response);
    return json.data;
  },

  create: async (data: CreateApiKeyInput): Promise<ApiKeyResponse & { key: string }> => {
    const response = await fetchWithAuth(`${API_URL}/core/api-keys`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error?.error?.message || error?.message || 'Erreur création clé API');
    }
    const json = await safeJsonParse(response);
    return json.data;
  },

  revoke: async (id: string): Promise<ApiKeyResponse> => {
    const response = await fetchWithAuth(`${API_URL}/core/api-keys/${encodeURIComponent(id)}/revoke`, {
      method: 'PATCH',
    });
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error?.error?.message || 'Erreur révocation clé API');
    }
    const json = await safeJsonParse(response);
    return json.data;
  },

  delete: async (id: string): Promise<void> => {
    const response = await fetchWithAuth(`${API_URL}/core/api-keys/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await safeJsonParse(response);
      throw new Error(error?.error?.message || 'Erreur suppression clé API');
    }
  },
};
